import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Admin credentials from environment variables
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
  const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD;
  
  // Check if admin credentials are configured
  const isAdminConfigured = !!(adminEmail && adminPassword);
  console.log('🔍 Admin configured:', isAdminConfigured);
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const startTime = Date.now();
      console.log('🔍 AuthContext: Auth state change event:', event);
      
      // Evitar procesamiento múltiple del mismo evento
      if (event === 'TOKEN_REFRESHED') {
        console.log('ℹ️ AuthContext: Token refreshed, skipping profile fetch');
        return;
      }
      
      if (session?.user) {
        console.log('✅ AuthContext: User session active, fetching profile...');
        await fetchUserProfile(session.user);
      } else {
        console.log('ℹ️ AuthContext: No user session, clearing user state');
        setUser(null);
      }
      setLoading(false);
      console.log(`✅ AuthContext: onAuthStateChange completed in ${Date.now() - startTime}ms`);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getSession = async () => {
    try {
      const startTime = Date.now();
      console.log('🔍 AuthContext: Getting session...');
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        console.log('✅ AuthContext: Session found, fetching user profile...');
        await fetchUserProfile(session.user);
      } else {
        console.log('ℹ️ AuthContext: No active session found');
      }
      console.log(`✅ AuthContext: getSession completed in ${Date.now() - startTime}ms`);
    } catch (error) {
      console.error('Error getting session:', error);
      console.error('Session error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async (authUser: SupabaseUser) => {
    try {
      const startTime = Date.now();
      console.log('🔍 AuthContext: Fetching user profile for:', authUser.id);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error) {
        console.error('❌ AuthContext: Error fetching user profile from public.users:', error);
        // If the profile is not found (PGRST116), it means the trigger might have failed or there's a delay.
        // We should not attempt to re-insert from the client here if we rely on the database trigger.
        if (error.code === 'PGRST116') {
          console.warn('⚠️ AuthContext: User profile not found in public.users after sign-up. This usually means the `handle_new_user` trigger in Supabase might not be configured correctly or failed to execute. Please verify your Supabase triggers.');
        } else {
          // Log other types of errors for debugging
          console.error('Profile error details:', { message: error.message, code: error.code, details: error.details, hint: error.hint });
        }
        setUser(null); // Ensure user is null if profile cannot be fetched
        return;
      }

      console.log('✅ AuthContext: User profile fetched successfully');
      console.log('🔍 AuthContext: User data:', data);
      setUser({
        id: data.id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        created_at: data.created_at
      });
      console.log(`✅ AuthContext: fetchUserProfile completed in ${Date.now() - startTime}ms`);
    } catch (error) {
      console.error('❌ AuthContext: Unexpected error in fetchUserProfile:', error);
      console.error('❌ AuthContext: Error type:', typeof error);
      console.error('❌ AuthContext: Error constructor:', error.constructor.name);
      console.error('❌ AuthContext: Full error object:', JSON.stringify(error, null, 2));
    }
  };

  // Check if current user is admin based on email
  const isCurrentUserAdmin = () => {
    if (!user || !isAdminConfigured) return false;
    return user.email === adminEmail;
  };

  // Admin login function
  const adminLogin = async (email: string, password: string) => {
    console.log('🔍 AuthContext: Attempting admin login...');
    console.log('🔍 AuthContext: Admin configured:', isAdminConfigured);
    console.log('🔍 AuthContext: Admin email from env:', adminEmail);
    console.log('🔍 AuthContext: Login email:', email);
    console.log('🔍 AuthContext: Passwords match:', password === adminPassword);
    
    if (!isAdminConfigured) {
      console.log('❌ AuthContext: Admin credentials not configured');
      throw new Error('Admin credentials not configured');
    }
    
    if (email !== adminEmail || password !== adminPassword) {
      console.log('❌ AuthContext: Invalid admin credentials');
      throw new Error('Invalid admin credentials');
    }
    
    console.log('✅ AuthContext: Admin login successful');
    // For admin, we don't use Supabase auth, just set a special admin user
    setUser({
      id: 'admin-user',
      email: adminEmail,
      first_name: 'Admin',
      last_name: 'User',
      created_at: new Date().toISOString()
    });
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔍 AuthContext: signIn called with email:', email);
    console.log('🔍 AuthContext: Admin email configured:', adminEmail);
    console.log('🔍 AuthContext: Is admin email?', email === adminEmail);
    
    // Check if this is an admin login attempt
    if (isAdminConfigured && email === adminEmail) {
      console.log('🔍 AuthContext: Routing to admin login');
      await adminLogin(email, password);
      return;
    } else {
      // Only admin login is allowed
      throw new Error('Acceso denegado: Solo se permite el inicio de sesión de administrador.');
    }
  };

  const signOut = async () => {
    // If admin user, just clear the state
    if (user?.id === 'admin-user') {
      setUser(null);
      return;
    }
    
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    isAdmin: isCurrentUserAdmin(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};